(() => {
  const SUMMARY_ROOT_SELECTOR = '.docapp-cart-with-coupon-summary';
  const COUPON_CONTAINER_SELECTOR = '.docapp-coupon-input';
  const CUSTOM_SUMMARY_CLASS = 'tci-cart-summary';
  const HIDDEN_CLASS = 'tci-cart-summary-hidden';
  let renderQueued = false;

  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  function formatMoney(cents, currency) {
    const amount = Number(cents || 0) / 100;
    const resolvedCurrency =
      currency ||
      window.Shopify?.currency?.active ||
      document.documentElement.getAttribute('data-currency') ||
      'CAD';

    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: resolvedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  function collectDiscounts(cart) {
    const groupedDiscounts = new Map();

    const addDiscount = (title, amount) => {
      const normalizedTitle = String(title || '').trim();
      const normalizedAmount = Number(amount || 0);

      if (!normalizedTitle || normalizedAmount <= 0) {
        return;
      }

      groupedDiscounts.set(
        normalizedTitle,
        (groupedDiscounts.get(normalizedTitle) || 0) + normalizedAmount
      );
    };

    (cart.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        addDiscount(allocation?.discount_application?.title, allocation?.amount);
      });
    });

    (cart.cart_level_discount_applications || []).forEach((application) => {
      addDiscount(application?.title, application?.total_allocated_amount);
    });

    return Array.from(groupedDiscounts.entries()).map(([title, amount]) => ({
      title,
      amount
    }));
  }

  function buildSummaryMarkup(cart) {
    const currency = cart.currency || 'CAD';
    const discounts = collectDiscounts(cart);
    const discountMarkup = discounts.length
      ? `
        <ul class="cart-discount-list tci-cart-summary__discounts">
          ${discounts
            .map(
              (discount) => `
                <li class="cart-discount cart-discount--inline">
                  <span class="cart-discount__label">
                    <span class="cart-discount__title">${escapeHtml(discount.title)}</span>
                  </span>
                  <span class="cart-discount__amount theme-money">-${formatMoney(discount.amount, currency)}</span>
                </li>
              `
            )
            .join('')}
        </ul>
      `
      : '';

    const subtotalMarkup = `
      <div class="subtotal h4 heading-font opposing-items">
        <span>Subtotal</span>
        <span class="theme-money">${formatMoney(cart.original_total_price, currency)}</span>
      </div>
    `;

    const estimatedTotalMarkup = `
      <div class="subtotal h4 heading-font opposing-items tci-cart-summary__estimated-total">
        <span>EST. TOTAL</span>
        <span class="tci-cart-summary__amounts">
          ${
            Number(cart.original_total_price) !== Number(cart.total_price)
              ? `<span class="theme-money struck-out-price small-text">${formatMoney(cart.original_total_price, currency)}</span>`
              : ''
          }
          <span class="theme-money">${formatMoney(cart.total_price, currency)}</span>
        </span>
      </div>
    `;

    return `${subtotalMarkup}${discountMarkup}${estimatedTotalMarkup}`;
  }

  function ensureSummaryNode(couponContainer, root) {
    const existingNode = root.querySelector(`.${CUSTOM_SUMMARY_CLASS}`);

    if (existingNode) {
      return existingNode;
    }

    const summaryNode = document.createElement('div');
    summaryNode.className = CUSTOM_SUMMARY_CLASS;

    if (couponContainer && couponContainer.parentElement) {
      couponContainer.insertAdjacentElement('afterend', summaryNode);
    } else {
      root.append(summaryNode);
    }

    return summaryNode;
  }

  function hideBrokenSummary(couponContainer, root, summaryNode) {
    if (!couponContainer) {
      return;
    }

    let currentNode = couponContainer.nextElementSibling;

    while (currentNode) {
      const nextNode = currentNode.nextElementSibling;

      if (currentNode !== summaryNode) {
        currentNode.classList.add(HIDDEN_CLASS);
      }

      currentNode = nextNode;
    }

    let ancestor = couponContainer.parentElement;

    while (ancestor && ancestor !== root) {
      let ancestorSibling = ancestor.nextElementSibling;

      while (ancestorSibling) {
        const nextSibling = ancestorSibling.nextElementSibling;

        if (ancestorSibling !== summaryNode) {
          ancestorSibling.classList.add(HIDDEN_CLASS);
        }

        ancestorSibling = nextSibling;
      }

      ancestor = ancestor.parentElement;
    }
  }

  async function renderSummary() {
    const roots = Array.from(document.querySelectorAll(SUMMARY_ROOT_SELECTOR));

    if (!roots.length) {
      return;
    }

    const response = await window.fetch('/cart.js', {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Cart summary refresh failed with status ${response.status}`);
    }

    const cart = await response.json();

    roots.forEach((root) => {
      const couponContainer = root.querySelector(COUPON_CONTAINER_SELECTOR);
      const summaryNode = ensureSummaryNode(couponContainer, root);
      hideBrokenSummary(couponContainer, root, summaryNode);
      summaryNode.innerHTML = buildSummaryMarkup(cart);
    });
  }

  function queueRender(delay = 0) {
    if (renderQueued) {
      return;
    }

    renderQueued = true;
    window.setTimeout(async () => {
      try {
        await renderSummary();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        renderQueued = false;
      }
    }, delay);
  }

  document.addEventListener('DOMContentLoaded', () => {
    queueRender(100);
  });

  document.addEventListener(
    'on:cart:after-merge',
    () => {
      queueRender(50);
    },
    true
  );

  document.addEventListener(
    'on:cart-drawer:after-open',
    () => {
      queueRender(150);
    },
    true
  );

  const observer = new MutationObserver(() => {
    if (document.querySelector(SUMMARY_ROOT_SELECTOR)) {
      queueRender(0);
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
